'use client';

import { useTranslation } from '@/app/i18n/client';
import { retryDynamicImport } from '@/lib/observability/retryDynamicImport';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';

import customFetch from '@/api/fetch';
import { OptimizedImage } from '@/components/atoms/media';
import FullScreenLoading from '@/components/fullScreenLoading';
import NextCartStepButton from '@/components/nextCartStepButton';
import { SessionButtons } from '@/components/sessionButtons';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { useConvenienceFee } from '@/context/ConvenienceFeeProvider';
import { useDiscountCoupon } from '@/context/DiscountCouponProvider';
import { useInPersonToken } from '@/context/InPersonTokenProvider';
import { usePartnerBenefit } from '@/context/PartnerBenefitProvider';
import { useTimeLeft } from '@/context/TimeLeftProvider';
import { useTrackingToken } from '@/context/TrackingTokenProvider';
import { useCacheManager } from '@/hooks/useCacheManager';
import useCart from '@/hooks/useCart';
import { useDebounce } from '@/hooks/useDebounce';
import useDonation from '@/hooks/useDonation';
import useGlobalState from '@/hooks/useGlobalState';
import { useIsMobileResolution } from '@/hooks/useIsMobileResolution';
import { useQueue } from '@/hooks/useQueue';
import { createExperienceCacheKey, createSeatsCacheKey } from '@/utils/cache-utils';
import { ErrorCodes } from '@/utils/constants';
import { toBrazilDateKey } from '@/utils/date';
import { getDateTimezone } from '@/utils/getDateTimezone';
import { getLocalStorage, setLocalStorage } from '@/utils/local-storage';
import { retryWithBackoff } from '@/utils/retryWithBackoff';
import { addSeatToSelection, collectSoldSeatIds, removeSeatIdsFromSelection } from '@/utils/seat-selection';
import { getSessionStorage } from '@/utils/session-storage';
import { setZeroHours } from '@/utils/setZeroHours';
import {
  EnumExperienceType,
  Experience,
  ExperienceConfiguration,
  FeeTypes,
  Order,
  Session,
  TicketType,
  TicketTypeWithOptionalTicketTypes,
} from '@/utils/types';
import { addDays, addMinutes, isSameDay } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Info } from 'lucide-react';
import { Matcher } from 'react-day-picker';
import useSWR from 'swr';
import PurchaseDetails from '../(purchase-details)';
import TicketsList from '../(tickets-list)';

const InfoFooter = dynamic(
  retryDynamicImport(() => import('@/components/infoFooter')),
  {
    loading: () => <div className='h-96' />,
  }
);

const SeatsMap = dynamic(
  retryDynamicImport(() => import('@/components/seatsMap')),
  { ssr: false }
);

export interface SeatsMapType {
  [seatRow: string]: {
    [seatColumn: number]: {
      id?: string;
      order?: number;
      closed: boolean;
      reserved: boolean;
      selected: boolean;
      status?: string;
      socketId?: string;
      startDate?: number;
      isSpecial: {
        wheelchairUser: boolean;
        elderly: boolean;
        reducedMobility: boolean;
        obesePerson: boolean;
      };
    };
  };
}

export interface EventSession {
  datetime: Date;
  available: boolean;
  configuration: {
    id: string;
    durationInMinutes: number;
    startTimeInMinutes: number;
    endTimeInMinutes: number;
  };
  assignedTickets: TicketType[];
}

export interface EventDataAPIResponse extends Experience {
  sessions: EventSession[];
  script: {
    salesRange: {
      from: Date;
      to: Date;
    };
    overlapDates?: Date[];
    unavailableDates?: number[] | string[];
    weekDaysClosed?: string[];
    configurationId?: ExperienceConfiguration[];
  };
}

interface IRemoveSeats {
  removeSeat: {
    seatId?: string;
    configurationId?: string;
    startsAt?: number;
  };
  removedAllCart: boolean;
}

interface EventosClientProps {
  lng: string;
  experienceId: string;
  initialExperience?: Experience;
}

export default function EventosClient({ lng, experienceId, initialExperience }: EventosClientProps) {
  const { t } = useTranslation(lng, ['tickets', 'experiences']);
  const { showToast } = useToast();

  const convertExperienceIdReplace = (experienceId as string).replaceAll('%22', '');

  const { getState } = useGlobalState();
  const { fn } = useTimeLeft();

  const { inPersonToken, handleValidateInPersonToken } = useInPersonToken();
  const { trackingToken, handleValidateTrackingToken } = useTrackingToken();
  const { discountCoupon } = useDiscountCoupon();
  const { partnerBenefit } = usePartnerBenefit();
  const { donation } = useDonation();
  const { convenienceFee } = useConvenienceFee();

  const { feeValue, feeType, ...restGlobalParameters } = getState('parameters');

  const {
    cart,
    cartSubtotalNoFee,
    cartSubtotalFee,
    addTicketToCart,
    removeTicketFromCart,
    createCartExperience,
    createCartSession,
    createCartTicketType,
    createCartConfigurationId,
    isAvailableTicketToAddCart,
    setCart,
  } = useCart();

  const { isMobile } = useIsMobileResolution();

  const [experience, setExperience] = useState<Experience | null>(initialExperience || null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<Session[]>([]);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [warnMessage, setWarnMessage] = useState<string | null>(null);
  const [disableNextButton, setDisableNextButton] = useState(false);
  const [disableTicketPlus, setDisableTicketPlus] = useState(false);
  const [cartTicketsNumber, setCartTicketsNumber] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(getDateTimezone());
  const [deselectedSeat, setDeselectedSeat] = useState<string | null>(null);
  const [deselectedSeatId, setDeselectedSeatId] = useState<string | null>(null);
  const [isFetchingExperience, setIsFetchingExperience] = useState(false);
  const [loadingSeats, setLoadingSeats] = useState<Set<string>>(new Set());

  const addSeatLoading = (seatIdentifier: string) => {
    setLoadingSeats((prev) => new Set(prev).add(seatIdentifier));
  };

  const removeSeatLoading = (seatIdentifier: string) => {
    setLoadingSeats((prev) => {
      const newSet = new Set(prev);
      newSet.delete(seatIdentifier);
      return newSet;
    });
  };

  const isSeatLoading = (seatIdentifier: string) => {
    return loadingSeats.has(seatIdentifier);
  };

  const { enqueue: queueOperation } = useQueue({
    maxRetries: 3,
    onError: (error: any) => console.error('Error to process operation:', error),
  });

  const { experienceCache, seatsCache } = useCacheManager({
    cleanupInterval: 5 * 60 * 1000, // 5 minutes
    maxExperienceCacheSizeKB: 2048, // 2MB
    maxSeatsCacheSizeKB: 1024, // 1MB
  });

  const [disabledDays, setDisabledDays] = useState<Matcher | Matcher[]>();
  const [overlapDates, setOverlapDates] = useState<Date[] | undefined>(undefined);
  const { blockSessionsByTimestamps } = useFlags();

  const [sessionId, setSessionId] = useState<string | null>(null);

  const handleFindNextAvailableDay = useCallback((date: Date, unavailableDates: Date[]) => {
    let nextDay = date;

    // Compara dias em wall-clock São Paulo via key YYYY-MM-DD (ver CLAUDE.md → Timezone).
    while (unavailableDates.some((d: Date) => toBrazilDateKey(d) === toBrazilDateKey(nextDay))) {
      nextDay = addDays(nextDay, 1);
    }

    return setZeroHours(nextDay);
  }, []);

  useEffect(() => {
    if (trackingToken && experience) handleValidateTrackingToken(experience.experiencesType.name, experience.id);
  }, [trackingToken, experience]);

  useEffect(() => {
    if (inPersonToken && experience) handleValidateInPersonToken(experience.experiencesType.name, experience.id);
  }, [inPersonToken, experience]);

  const calcTicketFee = useCallback(
    (ticketType: any) => {
      if (inPersonToken || ticketType?.price?.cents === 0 || !convenienceFee) return { cents: 0, decimal: 0 };

      const hasExperienceCustomFee = experience?.overlapFee;
      const ticketFeeType = experience?.overlapFee ? experience?.feeType : feeType;
      const ticketFeeValue = experience?.overlapFee ? experience?.valueOverlapFee : feeValue;

      if (ticketFeeType === FeeTypes.FIXED_VALUE) {
        return {
          cents: hasExperienceCustomFee ? experience.valueOverlapFee : ticketFeeValue,
          decimal: hasExperienceCustomFee ? experience.valueOverlapFee! / 100 : ticketFeeValue / 100,
        };
      }
      if (ticketFeeType === FeeTypes.PERCENTAGE_VALUE) {
        const priceWithPercentageFee = ticketType?.price?.cents + (ticketType?.price?.cents * ticketFeeValue) / 100;
        return { cents: priceWithPercentageFee, decimal: priceWithPercentageFee / 100 };
      }
    },
    [experience, feeValue, feeType, convenienceFee]
  );

  const handleFetchExperienceData = async (date?: Date) => {
    setIsFetchingExperience(true);

    try {
      const selectedSessionParam = date ?? getDateTimezone(experience?.startDate || new Date());
      const cacheKey = createExperienceCacheKey(convertExperienceIdReplace, selectedSessionParam);

      const cachedData = experienceCache.get(cacheKey);
      if (cachedData) {
        const { experienceData, mappedSession, unavailableDates, salesRangeFrom, salesRangeTo, overlapDates } =
          cachedData;

        setOverlapDates(overlapDates);
        setDisabledDays([{ before: salesRangeFrom, after: salesRangeTo }, ...unavailableDates]);
        if (!date) setSelectedDate(salesRangeFrom);

        const sessionsToSelectedDate = mappedSession.filter(
          ({ startsAt, available }: any) =>
            isSameDay(startsAt, date || getDateTimezone(date || new Date())) && available
        );

        let filteredSessions = sessionsToSelectedDate;
        if (blockSessionsByTimestamps) {
          filteredSessions = mappedSession.filter((session: any) => {
            if (blockSessionsByTimestamps === undefined) return true;

            const flagValue = JSON.parse(blockSessionsByTimestamps);
            const experienceId = flagValue[0];

            if (experienceId !== convertExperienceIdReplace) return true;

            const isBlocked = flagValue.includes(session.startsAt);
            return !isBlocked;
          });
        }

        setFilteredSessions(filteredSessions);
        setSessions(filteredSessions);
        setExperience({
          ...experienceData,
          valueOverlapFee: inPersonToken || !convenienceFee ? 0 : experienceData?.valueOverlapFee,
        });

        setIsFetchingExperience(false);
        return;
      }

      const {
        sessions: apiResponseSessions,
        script,
        ...experienceData
      }: EventDataAPIResponse = await customFetch(
        `/event-session-to-seat?experienceId=${convertExperienceIdReplace}&selectedSession=${selectedSessionParam?.toISOString()}`
      );

      const unavailableDates = script.unavailableDates!.map((unavailableDate: string | number) =>
        getDateTimezone(unavailableDate)
      );
      const salesRangeFrom = getDateTimezone(handleFindNextAvailableDay(script.salesRange.from, unavailableDates));
      const salesRangeTo = getDateTimezone(script.salesRange.to);

      const overlapDatesProcessed = script?.overlapDates?.map((overlapDate: string | number | Date) =>
        getDateTimezone(overlapDate)
      );

      setOverlapDates(overlapDatesProcessed);
      setDisabledDays([{ before: salesRangeFrom, after: salesRangeTo }, ...unavailableDates]);
      if (!date) setSelectedDate(salesRangeFrom);

      const mappedSession: Session[] = apiResponseSessions.map((session: any) => ({
        ...session,
        startsAt: new Date(session.datetime).getTime(),
        endsAt: addMinutes(session.datetime, session.configuration.durationInMinutes).getTime(),
        configurationId: session.configuration.id,
        assignedTickets: session.assignedTickets.map((ticket: any) => ({
          ...ticket.TicketTypes,
          fee: calcTicketFee(ticket as any),
          price: { cents: ticket.TicketTypes.price, decimal: ticket.TicketTypes.price / 100 },
        })),
      }));

      experienceCache.set(cacheKey, {
        experienceData,
        mappedSession,
        unavailableDates,
        salesRangeFrom,
        salesRangeTo,
        overlapDates: overlapDatesProcessed,
      });

      const sessionsToSelectedDate = mappedSession.filter(
        ({ startsAt, available }) => isSameDay(startsAt, date || getDateTimezone(date || new Date())) && available
      );

      let filteredSessions = sessionsToSelectedDate;
      if (blockSessionsByTimestamps) {
        filteredSessions = mappedSession.filter((session: any) => {
          if (blockSessionsByTimestamps === undefined) return true;

          const flagValue = JSON.parse(blockSessionsByTimestamps);
          const experienceId = flagValue[0];

          if (experienceId !== convertExperienceIdReplace) return true;

          const isBlocked = flagValue.includes(session.startsAt);
          return !isBlocked;
        });
      }

      setFilteredSessions(filteredSessions);
      setSessions(filteredSessions);
      setExperience({
        ...experienceData,
        valueOverlapFee: inPersonToken || !convenienceFee ? 0 : experienceData?.valueOverlapFee,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsFetchingExperience(false);
    }
  };

  useEffect(() => {
    const sessionInCart = cart.find(
      (c: Order) =>
        c.experience.id === experience?.id &&
        c.startDate === selectedSession?.startsAt &&
        c.endDate === selectedSession?.endsAt
    );

    if (sessionInCart) {
      setCartTicketsNumber(sessionInCart.tickets.reduce((acc: any, { amount }: any) => (acc += amount), 0));
    } else {
      setCartTicketsNumber(0);
    }
  }, [cart, experience, selectedSession]);

  const handleUnReserveSeatDirectCall = async (data: IRemoveSeats) => {
    new Promise((resolve, reject) => {
      queueOperation(async () => {
        try {
          const response = await customFetch(`/removed-reservation-seats`, {
            method: 'POST',
            body: JSON.stringify({
              ...data,
              socketId: sessionId,
            }),
          });

          resolve(response);
        } catch (err) {
          reject(err);
        }
      }, 'high');
    });
  };

  useEffect(() => {
    handleFetchExperienceData();

    const loadUserSessionId = () => {
      let id = getLocalStorage('sessionId');

      if (!id) {
        const userAgent = navigator.userAgent;
        const language = navigator.language;
        const timestamp = Date.now();
        id = btoa(`${userAgent}-${language}-${timestamp}`);
        setLocalStorage('sessionId', id);
      }

      setSessionId(id);
    };

    loadUserSessionId();

    window.addEventListener('beforeunload', unReserveAllSeatsWhenPageIsClosedOrTimeLeftIsOver);

    return () => {
      window.removeEventListener('beforeunload', unReserveAllSeatsWhenPageIsClosedOrTimeLeftIsOver);
    };
  }, []);

  const [seatsLeftMap, setSeatsLeftMap] = useState<SeatsMapType>({});
  const [seatsCenterMap, setSeatsCenterMap] = useState<SeatsMapType>({});
  const [seatsRightMap, setSeatsRightMap] = useState<SeatsMapType>({});

  const collectSelectedSeats = (seatsMap: SeatsMapType) => {
    const selectedSeats: string[] = [];
    const selectedSeatsIds: string[] = [];

    Object.keys(seatsMap).forEach((row) => {
      Object.keys(seatsMap[row]).forEach((column) => {
        const seat = seatsMap[row][column as any];
        if (seat.selected && seat.id) {
          selectedSeats.push(`${row}-${column}`);
          selectedSeatsIds.push(seat.id);
        }
      });
    });

    return { selectedSeats, selectedSeatsIds };
  };

  const selectedSeats = useMemo(() => {
    const selectedSeatsLeft = collectSelectedSeats(seatsLeftMap);
    const selectedSeatsCenter = collectSelectedSeats(seatsCenterMap);
    const selectedSeatsRight = collectSelectedSeats(seatsRightMap);
    const combinedSelectedSeats = [
      ...selectedSeatsLeft.selectedSeats,
      ...selectedSeatsCenter.selectedSeats,
      ...selectedSeatsRight.selectedSeats,
    ];

    const combinedSelectedSeatsIds = [
      ...selectedSeatsLeft.selectedSeatsIds,
      ...selectedSeatsCenter.selectedSeatsIds,
      ...selectedSeatsRight.selectedSeatsIds,
    ];

    return { combinedSelectedSeats, combinedSelectedSeatsIds };
  }, [seatsRightMap, seatsLeftMap, seatsCenterMap]);

  const resetAllSeats = (): void => {
    const resetMap = (seatsMap: SeatsMapType) => {
      return Object.keys(seatsMap).reduce((acc: any, row: string) => {
        acc[row] = Object.keys(seatsMap[row]).reduce((rowAcc: any, column: string) => {
          const seat = seatsMap[row][Number(column)];
          rowAcc[column] = { ...seat, selected: false, reserved: false, status: 'AVAILABLE' };
          return rowAcc;
        }, {});
        return acc;
      }, {});
    };

    setSeatsLeftMap((prevMap) => resetMap(prevMap));
    setSeatsCenterMap((prevMap) => resetMap(prevMap));
    setSeatsRightMap((prevMap) => resetMap(prevMap));
  };

  useEffect(() => {
    if (
      selectedSeats.combinedSelectedSeats.length !== cartTicketsNumber ||
      !selectedDate ||
      !selectedSession ||
      filteredSessions.length === 0
    ) {
      setDisableNextButton(true);
      handleShowSelectMoreTicketsOrSeatsMessage(selectedSession);
    } else {
      setDisableNextButton(false);
    }

    if (
      selectedSeats.combinedSelectedSeats.length <= cartTicketsNumber ||
      !selectedDate ||
      !selectedSession ||
      filteredSessions.length === 0
    ) {
      setDisableTicketPlus(true);
      handleShowSelectMoreTicketsOrSeatsMessage(selectedSession);
    } else {
      setDisableTicketPlus(false);
    }
  }, [selectedSeats, cartTicketsNumber, selectedDate, selectedSession, filteredSessions]);

  const handleUnreservedSeatExpiredTime = async () => {
    new Promise((resolve, reject) => {
      queueOperation(async () => {
        try {
          await customFetch(`/check-seats-remove-expired`, {
            method: 'POST',
            body: JSON.stringify({
              socketId: sessionId,
            }),
          });

          resolve(true);
        } catch (error) {
          console.error('Error calling remove seat API:', error);
          reject(error);
        }
      }, 'high');
    });
  };

  const unReserveAllSeatsWhenPageIsClosedOrTimeLeftIsOver = async (event?: BeforeUnloadEvent) => {
    if (event) {
      event.preventDefault();
      event.returnValue = '';
      return;
    }
    await handleUnreservedSeatExpiredTime();
    resetAllSeats();
    setCart([]);
  };

  const clearTimeLeft = (condition: boolean, mustClearTimeLeft: boolean) => {
    const timeLeft: string | null = getSessionStorage('timeLeft');

    if (condition || timeLeft) {
      const { timeExpiredToCancelSeatsInMinutes } = restGlobalParameters;
      fn(
        timeLeft !== null ? Number(timeLeft) : (timeExpiredToCancelSeatsInMinutes || 20) * 60,
        unReserveAllSeatsWhenPageIsClosedOrTimeLeftIsOver,
        mustClearTimeLeft
      );
    }
  };

  useEffect(() => {
    clearTimeLeft(cart.length > 0, cart.length === 0);
  }, [cart]);

  const handleUpdateSeatsMap = (data: SeatsMapType, side: SeatsMapType): SeatsMapType => {
    const updatedSeatsMap = { ...data };

    Object.keys(updatedSeatsMap).forEach((row) => {
      Object.keys(updatedSeatsMap[row]).forEach((col) => {
        const currentSeat = side[row]?.[col as any];
        const newSeat = updatedSeatsMap[row][col as any];

        const apiSocketId = newSeat?.socketId;

        const isSameUser = apiSocketId === sessionId;
        const isSameDateAndHour = selectedSession?.startsAt === newSeat?.startDate;

        const isSelectedByReserve = isSameUser && newSeat?.status === 'RESERVED' && isSameDateAndHour;
        const isSelectedByAvailable =
          newSeat?.status === 'AVAILABLE' &&
          currentSeat?.status === 'AVAILABLE' &&
          isSameDateAndHour &&
          currentSeat?.selected;
        const isReserved = !isSameUser && newSeat?.status === 'RESERVED';

        updatedSeatsMap[row][col as any] = {
          ...newSeat,
          selected: isSelectedByReserve || isSelectedByAvailable,
          reserved: isReserved,
        };
      });
    });

    return updatedSeatsMap;
  };

  const loadSeatsLeftHttp = async (
    experienceId: string | string[],
    configurationId?: string,
    startsAt?: number,
    endsAt?: number
  ) => {
    try {
      const response = await retryWithBackoff(() =>
        customFetch(`/load-seats`, {
          method: 'POST',
          body: JSON.stringify({
            experienceId,
            configurationId,
            startDate: startsAt,
            endDate: endsAt,
            locationOfSeats: 'seatsLeftMap',
            socketId: sessionId,
          }),
          keepalive: true,
          signal: AbortSignal.timeout(10000),
        })
      );

      setSeatsLeftMap((currentMap) => {
        const updatedSeatsMap = handleUpdateSeatsMap(response, currentMap);
        return updatedSeatsMap;
      });

      return response;
    } catch (err) {
      console.error('Error loading seats left:', err);
      return null;
    }
  };

  const loadSeatsCenterHttp = async (
    experienceId: string | string[],
    configurationId?: string,
    startsAt?: number,
    endsAt?: number
  ) => {
    try {
      const response = await customFetch(`/load-seats`, {
        method: 'POST',
        body: JSON.stringify({
          experienceId,
          configurationId,
          startDate: startsAt,
          endDate: endsAt,
          locationOfSeats: 'seatsCenterMap',
          socketId: sessionId,
        }),
        keepalive: true,
      });

      setSeatsCenterMap((currentMap) => {
        const updatedSeatsMap = handleUpdateSeatsMap(response, currentMap);
        return updatedSeatsMap;
      });

      return response;
    } catch (err) {
      console.error(err);
    }
  };

  const loadSeatsRightHttp = async (
    experienceId: string | string[],
    configurationId?: string,
    startsAt?: number,
    endsAt?: number
  ) => {
    try {
      const response = await customFetch(`/load-seats`, {
        method: 'POST',
        body: JSON.stringify({
          experienceId,
          configurationId,
          startDate: startsAt,
          endDate: endsAt,
          locationOfSeats: 'seatsRightMap',
          socketId: sessionId,
        }),
        keepalive: true,
      });

      setSeatsRightMap((currentMap) => {
        const updatedSeatsMap = handleUpdateSeatsMap(response, currentMap);
        return updatedSeatsMap;
      });

      return response;
    } catch (err) {
      console.error(err);
    }
  };

  const fetcher = async () => {
    const seatsCacheKey =
      selectedSession && selectedSession.configurationId
        ? createSeatsCacheKey(convertExperienceIdReplace, selectedSession.configurationId, selectedSession.startsAt)
        : null;

    if (seatsCacheKey) {
      const cachedSeats = seatsCache.get(seatsCacheKey);
      if (cachedSeats && !isValidating) {
        const { leftSeats, centerSeats, rightSeats } = cachedSeats;

        setSeatsLeftMap((currentMap) => handleUpdateSeatsMap(leftSeats, currentMap));
        setSeatsCenterMap((currentMap) => handleUpdateSeatsMap(centerSeats, currentMap));
        setSeatsRightMap((currentMap) => handleUpdateSeatsMap(rightSeats, currentMap));
      }
    }

    const loadLeftSeats = loadSeatsLeftHttp(
      experienceId,
      selectedSession?.configurationId,
      selectedSession?.startsAt,
      selectedSession?.endsAt
    );
    const loadCenterSeats = loadSeatsCenterHttp(
      experienceId,
      selectedSession?.configurationId,
      selectedSession?.startsAt,
      selectedSession?.endsAt
    );
    const loadRightSeats = loadSeatsRightHttp(
      experienceId,
      selectedSession?.configurationId,
      selectedSession?.startsAt,
      selectedSession?.endsAt
    );

    const results = await Promise.allSettled([loadLeftSeats, loadCenterSeats, loadRightSeats]);
    const allRequestsFulfilled = results.every((result) => result.status === 'fulfilled');

    if (seatsCacheKey && allRequestsFulfilled) {
      const [leftResult, centerResult, rightResult] = results as { value: any }[];

      seatsCache.set(seatsCacheKey, {
        leftSeats: leftResult.value,
        centerSeats: centerResult.value,
        rightSeats: rightResult.value,
      });
    }

    return results;
  };

  const { error, isLoading, isValidating, mutate } = useSWR('/load-seats', fetcher, {
    refreshInterval: 5000,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    errorRetryInterval: 5000,
    errorRetryCount: 10,
    dedupingInterval: 2000,
    loadingTimeout: 10000,
    shouldRetryOnError: (error) => {
      const notRetry = error?.status >= 400 && error?.status < 500 && error?.status !== 429;
      return !notRetry;
    },
    onError: (error) => {
      console.error('Error to load seats:', error);
      if (error?.status === 429) {
        showToast({
          title: 'Muitas requisições',
          description: 'Por favor, aguarde um momento antes de tentar novamente.',
          variant: 'warning',
        });
      }
    },
    onSuccess: () => {
      const seatsCacheKey =
        selectedSession && selectedSession.configurationId
          ? createSeatsCacheKey(convertExperienceIdReplace, selectedSession.configurationId, selectedSession.startsAt)
          : null;

      if (seatsCacheKey && selectedSeats.combinedSelectedSeats.length > 0) {
        seatsCache.remove(seatsCacheKey);
      }
    },
  });

  useEffect(() => {
    resetAllSeats();
    mutate();
  }, [selectedSession]);

  // Assento vendido não pode continuar no carrinho: uma venda que não limpou a seleção (erro após a
  // cobrança, aba recarregada com o diálogo aberto) faria a próxima venda da sessão reenviar esses ids.
  useEffect(() => {
    if (!experience || !selectedSession) return;

    const soldSeatIds = collectSoldSeatIds([seatsLeftMap, seatsCenterMap, seatsRightMap], selectedSession.startsAt);
    if (soldSeatIds.size === 0) return;

    const orderIndex = cart.findIndex(
      (order: Order) =>
        order.experience.id === experience.id &&
        order.startDate === selectedSession.startsAt &&
        order.configurationId === selectedSession.configurationId
    );
    if (orderIndex === -1) return;

    const order = cart[orderIndex];
    if (!order.selectedSeatsIds?.some((seatId: string) => soldSeatIds.has(seatId))) return;

    const nextCart = [...cart];
    nextCart[orderIndex] = { ...order, ...removeSeatIdsFromSelection(order, soldSeatIds) };
    setCart(nextCart);

    showToast({
      title: 'Assentos já vendidos removidos',
      description: 'O carrinho tinha assentos de uma venda anterior. Confira a seleção antes de continuar.',
      variant: 'warning',
    });
  }, [seatsLeftMap, seatsCenterMap, seatsRightMap, cart, experience, selectedSession]);

  useEffect(() => {
    if (error) {
      console.log('Error in fetcher: ', error);
    }
  }, [error]);

  const handleCalendarDateSelection = (date?: Date) => {
    handleFetchExperienceData(date);
    setSelectedDate(date || null);
    setSelectedSession(null);
  };

  const handleSelectSession = async (option?: Session) => {
    setSelectedSession(option || null);
  };

  const handleReserveSeats = async (cart: any) => {
    return new Promise((resolve, reject) => {
      queueOperation(async () => {
        try {
          if (cart.length === 0) {
            console.error('cart is empty');
            reject(new Error('Cart is empty'));
            return;
          }

          const response = await customFetch(`/reservation`, {
            method: 'POST',
            body: JSON.stringify({
              ...cart,
              socketId: sessionId,
            }),
          });

          if (response.reserved) {
            const reservation = response.reserved[0];
            const { experienceId, configurationId, startDate, endDate } = reservation;

            if (!experienceId || !configurationId || !startDate || !endDate) {
              console.error('Error to build room name: some values are undefined.');
              reject(new Error('Error to build room name: some values are undefined.'));
              return;
            }

            const startDateTimestamp = new Date(startDate).getTime();
            const endDateTimestamp = new Date(endDate).getTime();
            const roomName = `${experienceId}-${configurationId}-${startDateTimestamp}-${endDateTimestamp}`;
            resolve({ success: true, data: response.reserved, roomName });
          }
        } catch (error: any) {
          if (error?.code === Number(ErrorCodes.ERROR_RESERVATION_SEATS)) {
            showToast({
              title: 'Assento já reservado',
              description: 'Esse assento já foi reservado por outro visitante.',
              variant: 'warning',
            });

            await mutate();
          } else {
            showToast({
              title: 'Erro ao reservar assento',
              description: 'Ocorreu um erro ao processar a reserva',
              variant: 'error',
            });
          }
          reject(error);
        }
      }, 'high');
    });
  };

  const addToCart = async (
    ticketType: TicketTypeWithOptionalTicketTypes,
    amount: number,
    selectedSeats: string[],
    selectedSeatsIds: string[]
  ) => {
    if (!experience || !selectedSession) return;

    const ticketSource = ticketType.TicketTypes || ticketType;
    const cartExperience = createCartExperience(experience);

    const cartSession = createCartSession({
      ...selectedSession,
      endsAt: selectedSession.endsAt,
      startsAt: selectedSession.startsAt,
    });
    const cartTicketType = createCartTicketType({
      ...ticketType,
      price: ticketSource.price,
    });
    const cartConfiguration = createCartConfigurationId(selectedSession.configurationId ?? '');

    addTicketToCart(
      cartExperience,
      cartSession,
      cartTicketType,
      cartConfiguration,
      amount,
      discountCoupon,
      selectedSeats,
      selectedSeatsIds
    );
  };

  const removeFromCart = async (ticketType: TicketType, amount: number) => {
    if (!experience || !selectedSession) return;

    const cartExperience = createCartExperience(experience);
    const cartSession = createCartSession({
      ...selectedSession,
      endsAt: selectedSession.endsAt,
      startsAt: selectedSession.startsAt,
    });
    const cartTicketType = createCartTicketType(ticketType);

    removeTicketFromCart(cartExperience, cartSession, cartTicketType, amount);
  };

  const removeSeatFromCart = async (data: {
    seat: string;
    seatId: string;
    configurationId: string;
    experienceId: string;
    startDate: number;
  }) => {
    if (!experience || !selectedSession) return;

    const cartLocalStorage: any[] = getLocalStorage('cart');
    const cartIndex = cartLocalStorage.findIndex((cart: any) => {
      const isSameConfiguration = cart.configurationId === data.configurationId;
      const isSameExperience = cart.experience.id === data.experienceId;
      const isSameStartDate = cart.startDate === data.startDate;

      return isSameConfiguration && isSameExperience && isSameStartDate;
    });

    if (cartIndex === -1) {
      return;
    }

    const cart = cartLocalStorage[cartIndex];
    const cartWithoutSelectedSeats = cart.selectedSeats.filter((seat: string) => seat !== data.seat);
    const cartWithoutSelectedSeatsIds = cart.selectedSeatsIds.filter((seatId: string) => seatId !== data.seatId);

    if (cartWithoutSelectedSeats.length === 0 && cartWithoutSelectedSeatsIds.length === 0) {
      cartLocalStorage.splice(cartIndex, 1);
    } else {
      cartLocalStorage[cartIndex].selectedSeats = cartWithoutSelectedSeats;
      cartLocalStorage[cartIndex].selectedSeatsIds = cartWithoutSelectedSeatsIds;
    }

    setCart(cartLocalStorage);
  };

  const addSeatToCart = async (data: {
    seat: string;
    seatId: string;
    configurationId: string;
    experienceId: string;
    startDate: number;
  }) => {
    if (!experience || !selectedSession) return;

    const cartLocalStorage: any[] = getLocalStorage('cart');
    const cartIndex = cartLocalStorage.findIndex((cart: any) => {
      const isSameConfiguration = cart.configurationId === data.configurationId;
      const isSameExperience = cart.experience.id === data.experienceId;
      const isSameStartDate = cart.startDate === data.startDate;

      return isSameConfiguration && isSameExperience && isSameStartDate;
    });

    if (cartIndex === -1) {
      clearTimeLeft(true, false);
      return;
    }

    cartLocalStorage[cartIndex] = {
      ...cartLocalStorage[cartIndex],
      ...addSeatToSelection(cartLocalStorage[cartIndex], data.seat, data.seatId),
    };

    setCart(cartLocalStorage);
  };

  const getSeatStateBySeat = (seat: string, seatsMap: SeatsMapType) => {
    const [row, column] = seat.split('-');
    return seatsMap[row]?.[Number(column)];
  };

  const handleSelectSeatInLeft = async (
    seat: string,
    currentExperience?: Experience | null,
    currentSelectedSession?: Session | null
  ) => {
    try {
      const experienceToUse = currentExperience || experience;
      const sessionToUse = currentSelectedSession || selectedSession;
      const currentSeatState = getSeatStateBySeat(seat, seatsLeftMap);

      if (currentSeatState?.selected) {
        await handleUnReserveSeatDirectCall({
          removedAllCart: false,
          removeSeat: {
            seatId: currentSeatState?.id,
            configurationId: sessionToUse?.configurationId,
            startsAt: sessionToUse?.startsAt,
          },
        });

        removeSeatFromCart({
          seat,
          seatId: currentSeatState?.id!,
          configurationId: sessionToUse?.configurationId!,
          experienceId: experienceToUse?.id!,
          startDate: sessionToUse?.startsAt!,
        });

        setSeatsLeftMap((prevSeatsLeftMap) => {
          const [row, column] = seat.split('-');
          const prevSeat = prevSeatsLeftMap[row]?.[Number(column)];

          if (prevSeat) {
            const updatedSeat = {
              ...prevSeat,
              selected: false,
              status: 'AVAILABLE',
            };

            return {
              ...prevSeatsLeftMap,
              [row]: {
                ...prevSeatsLeftMap[row],
                [column]: updatedSeat,
              },
            };
          }
          return prevSeatsLeftMap;
        });

        setDeselectedSeat(seat);
        setDeselectedSeatId(currentSeatState?.id ?? '');
      } else {
        await handleReserveSeats([
          {
            configurationId: sessionToUse?.configurationId,
            experience: { id: experienceToUse?.id },
            selectedSeats: [seat],
            selectedSeatsIds: [currentSeatState?.id],
            startDate: sessionToUse?.startsAt,
            endDate: sessionToUse?.endsAt,
          },
        ]);

        addSeatToCart({
          seat,
          seatId: currentSeatState?.id!,
          configurationId: sessionToUse?.configurationId!,
          experienceId: experienceToUse?.id!,
          startDate: sessionToUse?.startsAt!,
        });

        setSeatsLeftMap((prevSeatsLeftMap) => {
          const [row, column] = seat.split('-');
          const prevSeat = prevSeatsLeftMap[row]?.[Number(column)];

          if (prevSeat) {
            if (prevSeat.closed || prevSeat.reserved) {
              return prevSeatsLeftMap;
            }

            const updatedSeat = {
              ...prevSeat,
              selected: true,
              status: 'RESERVED',
            };

            return {
              ...prevSeatsLeftMap,
              [row]: {
                ...prevSeatsLeftMap[row],
                [column]: updatedSeat,
              },
            };
          }
          return prevSeatsLeftMap;
        });
      }
    } catch (err) {
      console.error('Error to select seat in left:', err);
    } finally {
      setTimeout(() => {
        removeSeatLoading(seat);
      }, 300);
    }
  };

  const handleSelectSeatInCenter = async (
    seat: string,
    currentExperience?: Experience | null,
    currentSelectedSession?: Session | null
  ) => {
    try {
      const experienceToUse = currentExperience || experience;
      const sessionToUse = currentSelectedSession || selectedSession;
      const currentSeatState = getSeatStateBySeat(seat, seatsCenterMap);

      if (currentSeatState?.selected) {
        await handleUnReserveSeatDirectCall({
          removedAllCart: false,
          removeSeat: {
            seatId: currentSeatState?.id,
            configurationId: sessionToUse?.configurationId,
            startsAt: sessionToUse?.startsAt,
          },
        });

        removeSeatFromCart({
          seat,
          seatId: currentSeatState?.id!,
          configurationId: sessionToUse?.configurationId!,
          experienceId: experienceToUse?.id!,
          startDate: sessionToUse?.startsAt!,
        });

        setSeatsCenterMap((prevSeatsCenterMap) => {
          const [row, column] = seat.split('-');
          const prevSeat = prevSeatsCenterMap[row]?.[Number(column)];

          if (prevSeat) {
            const updatedSeat = {
              ...prevSeat,
              selected: false,
              status: 'AVAILABLE',
            };

            return {
              ...prevSeatsCenterMap,
              [row]: {
                ...prevSeatsCenterMap[row],
                [column]: updatedSeat,
              },
            };
          }
          return prevSeatsCenterMap;
        });

        setDeselectedSeat(seat);
        setDeselectedSeatId(currentSeatState?.id ?? '');
      } else {
        await handleReserveSeats([
          {
            configurationId: sessionToUse?.configurationId,
            experience: { id: experienceToUse?.id },
            selectedSeats: [seat],
            selectedSeatsIds: [currentSeatState?.id],
            startDate: sessionToUse?.startsAt,
            endDate: sessionToUse?.endsAt,
          },
        ]);

        addSeatToCart({
          seat,
          seatId: currentSeatState?.id!,
          configurationId: sessionToUse?.configurationId!,
          experienceId: experienceToUse?.id!,
          startDate: sessionToUse?.startsAt!,
        });

        setSeatsCenterMap((prevSeatsCenterMap) => {
          const [row, column] = seat.split('-');
          const prevSeat = prevSeatsCenterMap[row]?.[Number(column)];

          if (prevSeat) {
            if (prevSeat.closed || prevSeat.reserved) {
              return prevSeatsCenterMap;
            }

            const updatedSeat = {
              ...prevSeat,
              selected: true,
              status: 'RESERVED',
            };

            return {
              ...prevSeatsCenterMap,
              [row]: {
                ...prevSeatsCenterMap[row],
                [column]: updatedSeat,
              },
            };
          }
          return prevSeatsCenterMap;
        });
      }
    } catch (err) {
      console.error('Error to select seat in center:', err);
    } finally {
      setTimeout(() => {
        removeSeatLoading(seat);
      }, 300);
    }
  };

  const handleSelectSeatInRight = async (
    seat: string,
    currentExperience?: Experience | null,
    currentSelectedSession?: Session | null
  ) => {
    try {
      const experienceToUse = currentExperience || experience;
      const sessionToUse = currentSelectedSession || selectedSession;
      const currentSeatState = getSeatStateBySeat(seat, seatsRightMap);

      if (currentSeatState?.selected) {
        await handleUnReserveSeatDirectCall({
          removedAllCart: false,
          removeSeat: {
            seatId: currentSeatState?.id,
            configurationId: sessionToUse?.configurationId,
            startsAt: sessionToUse?.startsAt,
          },
        });

        removeSeatFromCart({
          seat,
          seatId: currentSeatState?.id!,
          configurationId: sessionToUse?.configurationId!,
          experienceId: experienceToUse?.id!,
          startDate: sessionToUse?.startsAt!,
        });

        setSeatsRightMap((prevSeatsRightMap) => {
          const [row, column] = seat.split('-');
          const prevSeat = prevSeatsRightMap[row]?.[Number(column)];

          if (prevSeat) {
            const updatedSeat = {
              ...prevSeat,
              selected: false,
              status: 'AVAILABLE',
            };

            return {
              ...prevSeatsRightMap,
              [row]: {
                ...prevSeatsRightMap[row],
                [column]: updatedSeat,
              },
            };
          }
          return prevSeatsRightMap;
        });

        setDeselectedSeat(seat);
        setDeselectedSeatId(currentSeatState?.id ?? '');
      } else {
        await handleReserveSeats([
          {
            configurationId: sessionToUse?.configurationId,
            experience: { id: experienceToUse?.id },
            selectedSeats: [seat],
            selectedSeatsIds: [currentSeatState?.id],
            startDate: sessionToUse?.startsAt,
            endDate: sessionToUse?.endsAt,
          },
        ]);

        addSeatToCart({
          seat,
          seatId: currentSeatState?.id!,
          configurationId: sessionToUse?.configurationId!,
          experienceId: experienceToUse?.id!,
          startDate: sessionToUse?.startsAt!,
        });

        setSeatsRightMap((prevSeatsRightMap) => {
          const [row, column] = seat.split('-');
          const prevSeat = prevSeatsRightMap[row]?.[Number(column)];

          if (prevSeat) {
            if (prevSeat.closed || prevSeat.reserved) {
              return prevSeatsRightMap;
            }

            const updatedSeat = {
              ...prevSeat,
              selected: true,
              status: 'RESERVED',
            };

            return {
              ...prevSeatsRightMap,
              [row]: {
                ...prevSeatsRightMap[row],
                [column]: updatedSeat,
              },
            };
          }
          return prevSeatsRightMap;
        });
      }
    } catch (err) {
      console.error('Error to select seat in right:', err);
    } finally {
      setTimeout(() => {
        removeSeatLoading(seat);
      }, 300);
    }
  };

  const handleShowSelectMoreTicketsOrSeatsMessage = (selectedSession?: Session | null) => {
    const diff = Math.abs(selectedSeats.combinedSelectedSeats.length - cartTicketsNumber);

    const hasSelectedSessionButNotSeat = selectedSeats.combinedSelectedSeats.length <= 0 && selectedSession?.available;
    if (hasSelectedSessionButNotSeat) return setWarnMessage(`(Selecione um assento)`);

    const hasMoreSeatsSelectedThanTickets = selectedSeats.combinedSelectedSeats.length > cartTicketsNumber;
    if (hasMoreSeatsSelectedThanTickets) return setWarnMessage(`(Selecione mais ${diff} ingresso(s))`);

    const hasMoreTicketsSelectedThanSeats = selectedSeats.combinedSelectedSeats.length < cartTicketsNumber;
    if (hasMoreTicketsSelectedThanSeats) return setWarnMessage(`(Selecione mais ${diff} assento(s))`);

    setWarnMessage(null);
  };

  const isAllReservedSeats = useCallback((seatsMap: SeatsMapType) => {
    return Object.keys(seatsMap).every((row) => {
      return Object.keys(seatsMap[row]).every((column) => {
        const seat = seatsMap[row][column as any];
        return seat.status === 'RESERVED' || seat.status === 'OCCUPIED';
      });
    });
  }, []);

  const isSoldOut =
    isAllReservedSeats(seatsLeftMap) && isAllReservedSeats(seatsCenterMap) && isAllReservedSeats(seatsRightMap);

  useEffect(() => {
    if (deselectedSeat && deselectedSeatId) {
      const cartLocalStorage = getLocalStorage('cart');

      const existSeatsInCart = cartLocalStorage.some((cart: any) => cart.selectedSeatsIds.includes(deselectedSeatId));

      if (existSeatsInCart) {
        const findCartIndex = cartLocalStorage.findIndex((date: any) => date.startDate === selectedSession?.startsAt);
        const findCart = cartLocalStorage[findCartIndex];

        removeFromCart(findCart.tickets[0], 1);
      }
    }
  }, [deselectedSeat, deselectedSeatId]);

  useEffect(() => {
    setSelectedSession((prev: any) => {
      if (!prev) return null;
      return {
        ...prev,
        assignedTickets: prev?.assignedTickets.map((ticket: any) => ({
          ...ticket,
          fee: calcTicketFee(ticket as any),
        })),
      };
    });
  }, [convenienceFee]);

  const debouncedHandleSelectSeatInLeft = useDebounce(
    (seat: string, session?: Session) => {
      if ((!selectedSession && !session) || isLoading || isValidating || isSeatLoading(seat)) return;

      addSeatLoading(seat);
      handleSelectSeatInLeft(seat, experience, selectedSession);
    },
    {
      delay: 300,
      leading: true,
      trailing: false,
    }
  );

  const debouncedHandleSelectSeatInCenter = useDebounce(
    (seat: string, session?: Session) => {
      if ((!selectedSession && !session) || isLoading || isValidating || isSeatLoading(seat)) return;

      addSeatLoading(seat);
      handleSelectSeatInCenter(seat, experience, selectedSession);
    },
    {
      delay: 300,
      leading: true,
      trailing: false,
    }
  );

  const debouncedHandleSelectSeatInRight = useDebounce(
    (seat: string, session?: Session) => {
      if ((!selectedSession && !session) || isLoading || isValidating || isSeatLoading(seat)) return;

      addSeatLoading(seat);
      handleSelectSeatInRight(seat, experience, selectedSession);
    },
    {
      delay: 300,
      leading: true,
      trailing: false,
    }
  );

  return (
    <div>
      {isFetchingExperience && !experience?.id && <FullScreenLoading text='' showLoading />}

      {experience?.id && (
        <div data-testid='eventos-page' className='h-screen w-full'>
          <div className='p-[20px] pt-[13px] max-[1024px]:p-[10px] max-sm:pt-[20px]'>
            <OptimizedImage
              src={isMobile ? experience?.onTextImages?.at(0) || experience.image : experience.image}
              alt={experience.name}
              quality={75}
              width={isMobile ? 1000 : 1920}
              height={isMobile ? 500 : 1080}
              priority
              fetchPriority='high'
              sizes='100vw'
              className='h-[45vh] w-full object-cover object-center max-[1024px]:h-56 max-sm:h-64'
            />

            {partnerBenefit && partnerBenefit.experience.id === convertExperienceIdReplace && (
              <div className='mt-4 rounded-lg border border-green-200 bg-green-50 p-4'>
                <div className='flex items-center gap-3'>
                  {partnerBenefit.partner.logoUrl && (
                    <OptimizedImage
                      src={partnerBenefit.partner.logoUrl}
                      alt=''
                      width={32}
                      height={32}
                      className='h-8 w-8 rounded'
                    />
                  )}
                  <div className='flex-1'>
                    <p className='font-semibold text-green-800'>Desconto parceiro — {partnerBenefit.partner.name}</p>
                    <p className='text-sm text-green-700'>
                      {partnerBenefit.benefit.type === 'DISCOUNT_PERCENTAGE'
                        ? `${partnerBenefit.benefit.value}% de desconto`
                        : `R$ ${(partnerBenefit.benefit.value / 100).toFixed(2)} de desconto`}
                      {' — '}
                      {partnerBenefit.benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Accordion
              type='single'
              collapsible
              defaultValue='item-1'
              className='mt-[20px] hidden w-full border-t border-gray2 text-foreground max-sm:block'>
              <AccordionItem value='item-1'>
                <AccordionTrigger className='text-2xl font-medium hover:text-primary hover:no-underline max-sm:text-lg'>
                  {t('description')}
                </AccordionTrigger>
                <AccordionContent className='pr-4'>
                  {experience ? (
                    <>
                      <p className='whitespace-pre-wrap text-base font-light'>{experience.summary}</p>
                      <p className='whitespace-pre-wrap text-base font-light'>{experience.description}</p>
                    </>
                  ) : (
                    <>
                      <Skeleton className='mb-2 h-16 w-full' />
                      <Skeleton className='h-32 w-full' />
                    </>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className='relative overflow-hidden border-gray-300 pb-0 max-[1024px]:mt-4 max-[1024px]:p-0 max-[1024px]:pt-2'>
              <div className='flex items-start justify-between gap-[20px] max-sm:flex-col-reverse'>
                <div className='w-full py-[20px] max-sm:hidden max-sm:py-[10px] md:max-w-[55vw]'>
                  <SeatsMap
                    isLoading={isLoading}
                    seatsLeftMap={seatsLeftMap}
                    seatsCenterMap={seatsCenterMap}
                    seatsRightMap={seatsRightMap}
                    selectedSession={selectedSession}
                    handleSelectSeatInLeft={debouncedHandleSelectSeatInLeft}
                    handleSelectSeatInCenter={debouncedHandleSelectSeatInCenter}
                    handleSelectSeatInRight={debouncedHandleSelectSeatInRight}
                    isMobile={isMobile}
                    isSeatLoading={isSeatLoading}
                  />

                  <Accordion
                    type='single'
                    collapsible
                    defaultValue='item-1'
                    className='mt-[20px] w-full border-t border-gray2 text-foreground'>
                    <AccordionItem value='item-1'>
                      <AccordionTrigger className='text-2xl font-medium hover:text-primary hover:no-underline max-sm:text-lg'>
                        {t('description')}
                      </AccordionTrigger>
                      <AccordionContent className='pr-4'>
                        {experience ? (
                          <>
                            <p className='whitespace-pre-wrap text-base font-light'>{experience.summary}</p>
                            <p className='whitespace-pre-wrap text-base font-light'>{experience.description}</p>
                          </>
                        ) : (
                          <>
                            <Skeleton className='mb-2 h-16 w-full' />
                            <Skeleton className='h-32 w-full' />
                          </>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <div className='mt-10 md:hidden'>
                    <NextCartStepButton
                      disabled={
                        disableNextButton ||
                        isSoldOut ||
                        !selectedSession ||
                        !selectedSeats.combinedSelectedSeats.length
                      }
                      cart={cart}
                      cartSubtotalNoFee={cartSubtotalNoFee}
                      cartSubtotalFee={cartSubtotalFee}
                      discountCoupon={discountCoupon}
                      lng={lng as string}
                      text={t('Prosseguir')}
                      donation={donation}
                    />
                  </div>
                </div>
                <div className='w-full max-w-[520px] py-[20px] max-sm:max-w-full'>
                  <Calendar
                    toMonth={addDays(
                      selectedDate || getDateTimezone(),
                      (experience?.canSellUntilDaysAdvance || 0) + (experience?.saleAnticipationDays || 0)
                    )}
                    startMonth={getDateTimezone(experience?.startDate) || getDateTimezone()}
                    className='w-full bg-background'
                    mode='single'
                    isEventWithReservedPlace
                    selected={selectedDate || getDateTimezone()}
                    onSelect={handleCalendarDateSelection}
                    disabled={disabledDays}
                    overlapDates={overlapDates}
                  />

                  <div className='my-10 hidden w-full items-start justify-between gap-10 max-[1024px]:flex-col max-sm:flex max-sm:flex-col'>
                    <div className='w-[50vw] max-[1024px]:w-full'>
                      <div className='mb-6 flex flex-col gap-2'>
                        <h3 className='text-3xl font-bold max-[1024px]:text-2xl'>{t('Horários')}</h3>
                        <span className='text-base text-zinc-500'>
                          Selecione uma sessão para visualizar os ingressos
                        </span>
                      </div>

                      {sessions.length > 0 && filteredSessions.length > 0 && !isFetchingExperience && (
                        <SessionButtons
                          disabled={
                            !isAvailableTicketToAddCart([EnumExperienceType.MASP_ESPETACULOS_E_EVENTOS_COM_LUGAR])
                          }
                          options={filteredSessions}
                          onSelect={handleSelectSession}
                          lng={lng as string}
                          experienceType={experience.experiencesType.name}
                        />
                      )}

                      {isFetchingExperience && (
                        <ul className='mb-4 space-y-4 max-sm:grid max-sm:grid-cols-2 max-sm:gap-4 max-sm:space-y-0'>
                          {Array.from({ length: 4 }).map((_, index) => (
                            <li key={index}>
                              <Skeleton className='flex h-15 w-full items-center justify-start border border-gray2 bg-transparent px-4 max-sm:h-16 max-sm:flex-col max-sm:justify-center max-sm:gap-2'>
                                <Skeleton className='h-8 w-20 max-sm:h-6 max-sm:w-20' />
                              </Skeleton>
                            </li>
                          ))}
                        </ul>
                      )}

                      {!filteredSessions.length && !isFetchingExperience && (
                        <div className='mt-4 bg-red-700/20 p-4 text-lg'>
                          <p className='flex items-center gap-2 text-center text-foreground max-md:text-left max-sm:text-sm'>
                            <span>
                              <Info size={20} />
                            </span>
                            Nenhuma sessão disponível para essa data
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <SeatsMap
                    className='mt-4 hidden max-sm:block'
                    isLoading={isLoading}
                    seatsLeftMap={seatsLeftMap}
                    seatsCenterMap={seatsCenterMap}
                    seatsRightMap={seatsRightMap}
                    selectedSession={selectedSession}
                    handleSelectSeatInLeft={debouncedHandleSelectSeatInLeft}
                    handleSelectSeatInCenter={debouncedHandleSelectSeatInCenter}
                    handleSelectSeatInRight={debouncedHandleSelectSeatInRight}
                    isMobile={isMobile}
                    isSeatLoading={isSeatLoading}
                  />

                  <PurchaseDetails
                    className='my-10 hidden max-sm:block'
                    selectedSeats={selectedSeats.combinedSelectedSeats}
                    selectedSession={selectedSession}
                    lng={lng as string}
                  />

                  <div className='flex w-full items-start justify-between gap-10 pt-[20px] max-[1024px]:flex-col max-sm:flex-col'>
                    <div className='w-[50vw] max-[1024px]:w-full'>
                      <div className='w-full max-sm:hidden'>
                        <div className='mb-6 flex flex-col gap-2'>
                          <h3 className='text-3xl font-bold max-[1024px]:text-2xl'>{t('Horários')}</h3>
                          <span className='text-base text-zinc-500'>
                            Selecione uma sessão para visualizar os ingressos
                          </span>
                        </div>

                        {sessions.length > 0 && filteredSessions.length > 0 && !isFetchingExperience && (
                          <SessionButtons
                            disabled={
                              !isAvailableTicketToAddCart([EnumExperienceType.MASP_ESPETACULOS_E_EVENTOS_COM_LUGAR])
                            }
                            options={filteredSessions}
                            onSelect={handleSelectSession}
                            lng={lng as string}
                            experienceType={experience.experiencesType.name}
                          />
                        )}

                        {isFetchingExperience && (
                          <ul className='mb-4 space-y-4 max-sm:grid max-sm:grid-cols-2 max-sm:gap-4 max-sm:space-y-0'>
                            {Array.from({ length: 4 }).map((_, index) => (
                              <li key={index}>
                                <Skeleton className='flex h-15 w-full items-center justify-start border border-gray2 bg-transparent px-4 max-sm:h-16 max-sm:flex-col max-sm:justify-center max-sm:gap-2'>
                                  <Skeleton className='h-8 w-20 max-sm:h-6 max-sm:w-20' />
                                </Skeleton>
                              </li>
                            ))}
                          </ul>
                        )}

                        {!filteredSessions.length && !isFetchingExperience && (
                          <div className='mt-4 bg-red-700/20 p-4 text-lg'>
                            <p className='flex items-center gap-2 text-center text-foreground max-md:text-left max-sm:text-sm'>
                              <span>
                                <Info size={20} />
                              </span>
                              Nenhuma sessão disponível para essa data
                            </p>
                          </div>
                        )}
                      </div>

                      {selectedSession && (
                        <>
                          <div className='mt-10 flex w-full flex-col space-y-4 max-[1024px]:w-full max-sm:mt-4'>
                            <TicketsList
                              isAvailableTicketToAddCart={isAvailableTicketToAddCart}
                              selectedSession={selectedSession}
                              selectedSeats={selectedSeats.combinedSelectedSeats}
                              cartTicketsNumber={cartTicketsNumber}
                              warnMessage={warnMessage}
                              experience={experience}
                              disableTicketPlus={disableTicketPlus}
                              addToCart={(ticket, amount) =>
                                addToCart(
                                  ticket,
                                  amount,
                                  selectedSeats.combinedSelectedSeats,
                                  selectedSeats.combinedSelectedSeatsIds
                                )
                              }
                              removeFromCart={removeFromCart}
                            />
                          </div>

                          <PurchaseDetails
                            className='mt-10 max-sm:hidden'
                            selectedSeats={selectedSeats.combinedSelectedSeats}
                            selectedSession={selectedSession}
                            lng={lng as string}
                          />
                        </>
                      )}

                      <div className='mt-10'>
                        <NextCartStepButton
                          disabled={
                            disableNextButton ||
                            isSoldOut ||
                            !selectedSession ||
                            !selectedSeats.combinedSelectedSeats.length
                          }
                          cart={cart}
                          cartSubtotalNoFee={cartSubtotalNoFee}
                          cartSubtotalFee={cartSubtotalFee}
                          discountCoupon={discountCoupon}
                          lng={lng as string}
                          text={t('Prosseguir')}
                          donation={donation}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <InfoFooter />
        </div>
      )}
    </div>
  );
}
